
import Vue from "vue";
import { loadPgFeeRates, calculatePgFee, PgFeeSummary } from "@/utils/pgFee";
import { REFUND_AMOUNT_FIELDS } from "@/utils/refundAmountSummary";
import eventBus from "@/eventBus";
import SettlementSummaryCard from "./SettlementSummaryCard.vue";
import SettlementSummaryChip from "./SettlementSummaryChip.vue";
import SettlementSummaryComparison from "./SettlementSummaryComparison.vue";
import SettlementSummaryMetric from "./SettlementSummaryMetric.vue";

export default Vue.extend({
  name: "SummaryCards",
  components: {
    SettlementSummaryCard,
    SettlementSummaryChip,
    SettlementSummaryComparison,
    SettlementSummaryMetric,
  },
  props: {
    payment_composition_amount: { type: Number, default: 0 },
    order_count: { type: Number, default: 0 },
    refund_amount: { type: Number, default: 0 },
    refund_breakdown: {
      type: Object as () => Record<string, number>,
      default: () => ({}),
    },
    refund_count: { type: Number, default: 0 },
    total_amount: { type: Number, default: 0 },
    prev_flag: Boolean,
    prev_payment_composition_amount: { type: Number, default: 0 },
    prev_order_count: { type: Number, default: 0 },
    prev_refund_amount: { type: Number, default: 0 },
    prev_refund_count: { type: Number, default: 0 },
    compare_payment_composition_amount: { type: Number, default: 0 },
    compare_refund_amount: { type: Number, default: 0 },
    compare_amount: { type: Number, default: 0 },
    isCheckingPrev: Boolean,
    isFetchingPrev: Boolean,
    previousPeriodName: { type: String, default: "이전 기간" },
    previousRange: Object,
    isPrevFuture: Boolean,
    formattedRefundRate: String,
    refundRateBadgeClass: String,
    refundRateStatus: String,
    fee_summary: {
      type: Object as () => PgFeeSummary,
      default: undefined,
    },
    payment_summary: {
      type: Object as () => Record<string, any>,
      default: () => ({}),
    },
    prev_fee_summary: {
      type: Object as () => PgFeeSummary,
      default: undefined,
    },
  },
  data() {
    return {
      pgRates: loadPgFeeRates(),
      estimatedHelpDialog: false,
      effectiveRateHelpDialog: false,
      refundOwnedMallHelpDialog: false,
    };
  },
  computed: {
    refundAmountFields() {
      return REFUND_AMOUNT_FIELDS;
    },
    needsPreviousData(): boolean {
      return Boolean(
        !this.isCheckingPrev &&
          !this.isFetchingPrev &&
          !this.prev_flag &&
          this.previousRange?.start &&
          !this.isPrevFuture
      );
    },
    showsPreviousComparison(): boolean {
      return Boolean(
        this.prev_flag ||
          this.isCheckingPrev ||
          this.isFetchingPrev ||
          this.needsPreviousData
      );
    },
    paymentSummary(): Record<string, number> {
      const summary = (this.payment_summary || {}) as Record<string, any>;
      return {
        orderCount: Number(summary.orderCount) || 0,
        itemCount: Number(summary.itemCount) || 0,
        productAmount: Number(summary.productAmount) || 0,
        shippingAmount: Number(summary.shippingAmount) || 0,
        discountAmount: Number(summary.discountAmount) || 0,
        couponAmount: Number(summary.couponAmount) || 0,
        orderCompositionAmount: Number(summary.orderCompositionAmount) || 0,
        initialPaymentAmount:
          Number(summary.initialPaymentAmount ?? summary.paymentAmount) || 0,
        actualPaymentAmount:
          Number(summary.actualPaymentAmount ?? summary.paymentAmount) || 0,
        orderPaymentAmount: Number(summary.orderPaymentAmount) || 0,
        initialOrderPriceAmount:
          Number(
            summary.initialOrderPriceAmount ?? summary.initialProductAmount
          ) || 0,
        actualOrderPriceAmount:
          Number(
            summary.actualOrderPriceAmount ?? summary.actualProductAmount
          ) || 0,
        initialOrderCount:
          Number(summary.initialOrderCount ?? summary.orderCount) || 0,
        actualOrderCount:
          Number(summary.actualOrderCount ?? summary.orderCount) || 0,
        initialItemCount:
          Number(summary.initialItemCount ?? summary.itemCount) || 0,
        actualItemCount:
          Number(summary.actualItemCount ?? summary.itemCount) || 0,
        initialProductAmount:
          Number(summary.initialProductAmount ?? summary.productAmount) || 0,
        actualProductAmount:
          Number(summary.actualProductAmount ?? summary.productAmount) || 0,
        initialShippingAmount:
          Number(summary.initialShippingAmount ?? summary.shippingAmount) || 0,
        actualShippingAmount:
          Number(summary.actualShippingAmount ?? summary.shippingAmount) || 0,
        initialDiscountAmount:
          Number(summary.initialDiscountAmount ?? summary.discountAmount) || 0,
        actualDiscountAmount:
          Number(summary.actualDiscountAmount ?? summary.discountAmount) || 0,
        initialCouponAmount:
          Number(summary.initialCouponAmount ?? summary.couponAmount) || 0,
        actualCouponAmount:
          Number(summary.actualCouponAmount ?? summary.couponAmount) || 0,
        initialOrderCompositionAmount:
          Number(
            summary.initialOrderCompositionAmount ??
              summary.orderCompositionAmount
          ) || 0,
        actualOrderCompositionAmount:
          Number(
            summary.actualOrderCompositionAmount ??
              summary.orderCompositionAmount
          ) || 0,
      };
    },
    hasOrderCountDiff(): boolean {
      return (
        this.paymentSummary.initialOrderCount !==
        this.paymentSummary.actualOrderCount
      );
    },
    orderCountTooltip(): string {
      const init = this.paymentSummary.initialOrderCount;
      const act = this.paymentSummary.actualOrderCount;
      if (this.hasOrderCountDiff) {
        const diff = act - init;
        return `최초 주문수: ${init.toLocaleString()}건 ➔ 현재 주문수: ${act.toLocaleString()}건 (차이: ${
          diff > 0 ? "+" : ""
        }${diff.toLocaleString()}건)`;
      }
      return `주문수: ${act.toLocaleString()}건 (최초/현재 동일)`;
    },
    hasItemCountDiff(): boolean {
      return (
        this.paymentSummary.initialItemCount !==
        this.paymentSummary.actualItemCount
      );
    },
    itemCountTooltip(): string {
      const init = this.paymentSummary.initialItemCount;
      const act = this.paymentSummary.actualItemCount;
      if (this.hasItemCountDiff) {
        const diff = act - init;
        return `최초 품목수: ${init.toLocaleString()}개 ➔ 현재 품목수: ${act.toLocaleString()}개 (차이: ${
          diff > 0 ? "+" : ""
        }${diff.toLocaleString()}개)`;
      }
      return `품목수: ${act.toLocaleString()}개 (최초/현재 동일)`;
    },
    hasProductAmountDiff(): boolean {
      return (
        this.paymentSummary.initialProductAmount !==
        this.paymentSummary.actualProductAmount
      );
    },
    productAmountTooltip(): string {
      const init = this.paymentSummary.initialProductAmount;
      const act = this.paymentSummary.actualProductAmount;
      if (this.hasProductAmountDiff) {
        const diff = act - init;
        return `최초 상품구매금액: ${init.toLocaleString()}원 ➔ 현재 상품구매금액: ${act.toLocaleString()}원 (차이: ${
          diff > 0 ? "+" : "-"
        }${Math.abs(diff).toLocaleString()}원)`;
      }
      return `상품구매금액: ${act.toLocaleString()}원 (최초/현재 동일)`;
    },
    hasShippingAmountDiff(): boolean {
      return (
        this.paymentSummary.initialShippingAmount !==
        this.paymentSummary.actualShippingAmount
      );
    },
    shippingAmountTooltip(): string {
      const init = this.paymentSummary.initialShippingAmount;
      const act = this.paymentSummary.actualShippingAmount;
      if (this.hasShippingAmountDiff) {
        const diff = act - init;
        return `최초 배송비: ${init.toLocaleString()}원 ➔ 현재 배송비: ${act.toLocaleString()}원 (차이: ${
          diff > 0 ? "+" : "-"
        }${Math.abs(diff).toLocaleString()}원)`;
      }
      return `배송비: ${act.toLocaleString()}원 (최초/현재 동일)`;
    },
    hasDiscountAmountDiff(): boolean {
      return (
        this.paymentSummary.initialDiscountAmount !==
        this.paymentSummary.actualDiscountAmount
      );
    },
    discountAmountTooltip(): string {
      const init = this.paymentSummary.initialDiscountAmount;
      const act = this.paymentSummary.actualDiscountAmount;
      if (this.hasDiscountAmountDiff) {
        const diff = act - init;
        return `최초 할인: -${init.toLocaleString()}원 ➔ 현재 할인: -${act.toLocaleString()}원 (차이: ${
          diff > 0 ? "+" : "-"
        }${Math.abs(diff).toLocaleString()}원)`;
      }
      return `할인: -${act.toLocaleString()}원 (최초/현재 동일)`;
    },
    hasCouponAmountDiff(): boolean {
      return (
        this.paymentSummary.initialCouponAmount !==
        this.paymentSummary.actualCouponAmount
      );
    },
    couponAmountTooltip(): string {
      const init = this.paymentSummary.initialCouponAmount;
      const act = this.paymentSummary.actualCouponAmount;
      if (this.hasCouponAmountDiff) {
        const diff = act - init;
        return `최초 쿠폰: -${init.toLocaleString()}원 ➔ 현재 쿠폰: -${act.toLocaleString()}원 (차이: ${
          diff > 0 ? "+" : "-"
        }${Math.abs(diff).toLocaleString()}원)`;
      }
      return `쿠폰: -${act.toLocaleString()}원 (최초/현재 동일)`;
    },
    initialCompositionAmount(): number {
      return (
        this.paymentSummary.initialOrderCompositionAmount ||
        this.paymentSummary.initialProductAmount +
          this.paymentSummary.initialShippingAmount -
          this.paymentSummary.initialDiscountAmount -
          this.paymentSummary.initialCouponAmount ||
        0
      );
    },
    actualCompositionAmount(): number {
      return (
        this.paymentSummary.actualOrderCompositionAmount ||
        this.paymentSummary.actualProductAmount +
          this.paymentSummary.actualShippingAmount -
          this.paymentSummary.actualDiscountAmount -
          this.paymentSummary.actualCouponAmount ||
        0
      );
    },
    initialCompositionFormulaValues(): string {
      const initProd = (
        this.paymentSummary.initialProductAmount || 0
      ).toLocaleString();
      const initShip = (
        this.paymentSummary.initialShippingAmount || 0
      ).toLocaleString();
      const initDisc = (
        this.paymentSummary.initialDiscountAmount || 0
      ).toLocaleString();
      const initCoup = (
        this.paymentSummary.initialCouponAmount || 0
      ).toLocaleString();
      return `${initProd}원 + ${initShip}원 - ${initDisc}원 - ${initCoup}원`;
    },
    actualCompositionFormulaValues(): string {
      const actProd = (
        this.paymentSummary.actualProductAmount || 0
      ).toLocaleString();
      const actShip = (
        this.paymentSummary.actualShippingAmount || 0
      ).toLocaleString();
      const actDisc = (
        this.paymentSummary.actualDiscountAmount || 0
      ).toLocaleString();
      const actCoup = (
        this.paymentSummary.actualCouponAmount || 0
      ).toLocaleString();
      return `${actProd}원 + ${actShip}원 - ${actDisc}원 - ${actCoup}원`;
    },
    settlementPaymentAmount(): number {
      return this.payment_composition_amount || 0;
    },
    paymentDifferenceAmount(): number {
      return this.actualCompositionAmount - this.initialCompositionAmount;
    },
    formattedPaymentDifference(): string {
      const amount = this.paymentDifferenceAmount;
      if (amount === 0) return "0원";
      return `${amount > 0 ? "+" : "-"}${Math.abs(amount).toLocaleString()}원`;
    },
    estimatedPgFee(): number {
      if (this.fee_summary && typeof this.fee_summary.fee === "number") {
        return this.fee_summary.fee;
      }
      if (
        !this.payment_composition_amount ||
        this.payment_composition_amount <= 0
      )
        return 0;
      return calculatePgFee(
        "신용카드",
        this.payment_composition_amount,
        this.pgRates
      );
    },
    effectivePgFeeRate(): string {
      const paymentCompositionAmount = this.payment_composition_amount || 0;
      if (paymentCompositionAmount <= 0) return "0.0";
      return ((this.estimatedPgFee / paymentCompositionAmount) * 100).toFixed(
        1
      );
    },
    prevEstimatedPgFee(): number {
      if (
        this.prev_fee_summary &&
        typeof this.prev_fee_summary.fee === "number"
      ) {
        return this.prev_fee_summary.fee;
      }
      if (
        !this.prev_payment_composition_amount ||
        this.prev_payment_composition_amount <= 0
      )
        return 0;
      return calculatePgFee(
        "신용카드",
        this.prev_payment_composition_amount,
        this.pgRates
      );
    },
    feeSummaryEstimatedCount(): number {
      return this.fee_summary?.estimatedCount || 0;
    },
    feeSummaryUnavailableCount(): number {
      return this.fee_summary?.unavailableCount || 0;
    },
    compare_pg_fee(): number {
      return this.estimatedPgFee - this.prevEstimatedPgFee;
    },
    pgFeeGrowthRateText(): string {
      if (!this.prevEstimatedPgFee || this.prevEstimatedPgFee <= 0)
        return "0.0%";
      const rate = (
        (this.compare_pg_fee / this.prevEstimatedPgFee) *
        100
      ).toFixed(1);
      return Number(rate) > 0 ? `+${rate}%` : `${rate}%`;
    },
    feeAdjustedAmount(): number {
      return this.netSalesAmount - this.estimatedPgFee;
    },
    refundAmountAbs(): number {
      return Math.abs(this.refund_amount || 0);
    },
    refundRateFormulaValues(): string {
      const paymentAmount = this.settlementPaymentAmount;
      const refundAmount = this.refundAmountAbs;
      return `${refundAmount.toLocaleString()}원 ÷ ${paymentAmount.toLocaleString()}원 × 100`;
    },
    netSalesAmount(): number {
      return this.settlementPaymentAmount - this.refundAmountAbs;
    },
    prevRefundAmountAbs(): number {
      return Math.abs(this.prev_refund_amount || 0);
    },
    prevNetSalesAmount(): number {
      return (
        (this.prev_payment_composition_amount || 0) - this.prevRefundAmountAbs
      );
    },
    compare_net_sales_amount(): number {
      return this.netSalesAmount - this.prevNetSalesAmount;
    },
    compare_refund_total_amount(): number {
      return this.refundAmountAbs - this.prevRefundAmountAbs;
    },
    netSalesGrowthRateText(): string {
      if (!this.prevNetSalesAmount || this.prevNetSalesAmount <= 0)
        return "0.0%";
      const rate = (
        (this.compare_net_sales_amount / this.prevNetSalesAmount) *
        100
      ).toFixed(1);
      return Number(rate) > 0 ? `+${rate}%` : `${rate}%`;
    },
    refundGrowthRateText(): string {
      if (!this.prevRefundAmountAbs || this.prevRefundAmountAbs <= 0)
        return "0.0%";
      const rate = (
        (this.compare_refund_total_amount / this.prevRefundAmountAbs) *
        100
      ).toFixed(1);
      return Number(rate) > 0 ? `+${rate}%` : `${rate}%`;
    },
    prevFeeAdjustedAmount(): number {
      return this.prevNetSalesAmount - this.prevEstimatedPgFee;
    },
    compare_fee_adjusted_amount(): number {
      return this.feeAdjustedAmount - this.prevFeeAdjustedAmount;
    },
    feeAdjustedGrowthRateText(): string {
      if (!this.prevFeeAdjustedAmount || this.prevFeeAdjustedAmount <= 0)
        return "0.0%";
      const rate = (
        (this.compare_fee_adjusted_amount / this.prevFeeAdjustedAmount) *
        100
      ).toFixed(1);
      return Number(rate) > 0 ? `+${rate}%` : `${rate}%`;
    },
    paymentCompositionGrowthRateText(): string {
      const prev =
        (this.payment_composition_amount || 0) -
        this.compare_payment_composition_amount;
      if (!prev || prev <= 0) return "0.0%";
      const rate = (
        (this.compare_payment_composition_amount / prev) *
        100
      ).toFixed(1);
      return Number(rate) > 0 ? `+${rate}%` : `${rate}%`;
    },
  },
  methods: {
    formatCurrency(amount: number): string {
      return `${Number(amount || 0).toLocaleString()}원`;
    },
    formatCurrencyCompact(amount: number): string {
      return `${Number(amount || 0).toLocaleString()}원`;
    },
    refundBreakdownValue(key: string): number {
      return Math.abs(Number(this.refund_breakdown?.[key]) || 0);
    },
    openPgFeeSettings() {
      eventBus.$emit("open-pg-fee-settings");
    },
    openEstimatedHelp() {
      this.estimatedHelpDialog = true;
    },
    closeEstimatedHelp() {
      this.estimatedHelpDialog = false;
    },
    openEffectiveRateHelp() {
      this.effectiveRateHelpDialog = true;
    },
    closeEffectiveRateHelp() {
      this.effectiveRateHelpDialog = false;
    },
    openRefundOwnedMallHelp() {
      this.refundOwnedMallHelpDialog = true;
    },
    closeRefundOwnedMallHelp() {
      this.refundOwnedMallHelpDialog = false;
    },
  },
  created() {
    eventBus.$on("pg-fee-updated", () => {
      this.pgRates = loadPgFeeRates();
    });
  },
  beforeDestroy() {
    eventBus.$off("pg-fee-updated");
  },
});
